import { CancelRounded } from '@mui/icons-material';
import { capitalize, Dialog, IconButton, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import { CommissionPicks, Partner, ResponseWithAdditionalData } from '../../@types/api';
import { COMMISSION_PICKS, LS_KEYS, QUERY_KEYS } from '../../constants';
import { createNumberColumn } from '../../helpers/columns';
import usePersist from '../../hooks/usePersist';
import { createColumn } from '../../utils';
import { getData } from '../../utils/api';
import FullScreenLoader from '../atoms/loaders/FullScreenLoader';
import { DayjsTimestamp } from '../molecules/DateRangeSelect';
import { FilterAgentsReportData } from './FilterAgentsReports';
import StyledDataGrid from './StyledDataGrid';
import { dayjs } from '../../config/dayjs';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      width: '100%',
      borderRadius: 2,
      boxShadow: 0,
      backgroundColor: 'transparent',
    },
  },
  table: {
    '& .MuiDataGrid-row:nth-of-type(even)': {
      backgroundColor: 'background.light',
      '&:hover': {
        backgroundColor: 'background.light',
      },
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: '#fff',
    },
    '& .MuiDataGrid-columnHeader': {
      '&:last-child': {
        width: '100% !important',
        maxWidth: 'unset !important',
      },
    },
    '& .MuiDataGrid-columnHeadersInner': {
      width: '100%',
      '> div': {
        width: '100%',
      },
    },
  },
  icon: {
    alignSelf: 'flex-end',
    color: 'primary.container',
    cursor: 'pointer',
  },
};

type Props = {
  closeModal: () => void;
  dateRangeFilter: DayjsTimestamp;
  id: string;
  commission: CommissionPicks<number>;
  playerId?: string;
};

type Response = CommissionPicks<number> & { total: number; name: string };
type AdditionalData = { agent: Pick<Partner, 'id' | 'fullName'> };

const skipFormattingForTicketsRow = (_value: unknown, row: Record<string, unknown>) => row.name === 'tickets';

const AgentReportsDetails = ({ id: agentId, closeModal, dateRangeFilter, commission, playerId }: Props) => {
  const { pathname } = useLocation();
  const formDataKey = `${LS_KEYS.appFormFilter}-${pathname}`;

  const { getPersistData } = usePersist(false);
  const savedFilterData = getPersistData<FilterAgentsReportData>(formDataKey);

  const { data, isPending } = useQuery({
    queryKey: [QUERY_KEYS.agentsReportDetails, agentId, playerId],

    queryFn: (): Promise<ResponseWithAdditionalData<Response, AdditionalData>> =>
      getData(`reports/partners/agents/${agentId}`, {
        fromTimestamp: dayjs(dateRangeFilter.fromTimestamp).valueOf(),
        toTimestamp: dayjs(dateRangeFilter.toTimestamp).valueOf(),
        playerId,
        ...savedFilterData,
      }),

    enabled: !!agentId,
  });

  const columns: GridColDef[] = [
    createColumn('', `Partner: ${data?.agent.fullName}`, { sortable: false, flex: 1.5 }),
    createColumn('name', 'Per Pick', {
      sortable: false,
      valueFormatter: (value, row, column, apiRef) => capitalize(value),
      flex: 1.5,
    }),
    ...COMMISSION_PICKS.map((pick) =>
      createNumberColumn(pick, `${pick} (${commission[pick] / 100}%)`, {
        sortable: false,
        skipFormatting: skipFormattingForTicketsRow,
      })
    ),
    createNumberColumn('total', 'Totals', {
      sortable: false,
      skipFormatting: skipFormattingForTicketsRow,
    }),
  ];

  if (isPending) return <FullScreenLoader />;

  return (
    <Dialog sx={styles.dialog} open fullWidth maxWidth='xl'>
      <Stack>
        <IconButton sx={styles.icon} onClick={closeModal}>
          <CancelRounded fontSize='large' />
        </IconButton>
        <StyledDataGrid
          columns={columns}
          rows={data?.items || []}
          getRowId={(params) => params.name}
          sx={styles.table}
        />
      </Stack>
    </Dialog>
  );
};

export default AgentReportsDetails;
